import React from 'react';
import SEO from '../components/SEO';
import './PriceTrackerDealAlerts.css';

const AndroidIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.523 15.341a.914.914 0 01-.913.912.912.912 0 01-.912-.912.913.913 0 01.912-.913.914.914 0 01.913.913zm-9.219 0a.914.914 0 01-.913.912.912.912 0 01-.912-.912.913.913 0 01.912-.913.914.914 0 01.913.913zM17.9 10l1.448-2.508a.302.302 0 00-.11-.412.302.302 0 00-.412.11l-1.468 2.542A8.965 8.965 0 0012 8.545c-1.97 0-3.79.635-5.258 1.687L5.274 7.19a.302.302 0 00-.412-.11.302.302 0 00-.11.412L6.2 10c-2.133 1.454-3.548 3.795-3.548 6.453 0 .303.017.602.048.898h18.6c.031-.296.048-.595.048-.898C21.348 13.795 19.933 11.454 17.9 10z"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const features = [
  { icon: '🔔', title: 'Price Drop Alerts', desc: 'Get notified the moment a tracked item falls in price. Set your target price and never miss a great deal again.' },
  { icon: '🎯', title: 'Target Price Tracking', desc: 'Set the exact price you want to pay and wait for the drop. Shop at the right time, every time.' },
  { icon: '📊', title: 'Price History & Insights', desc: 'See how a product\'s price changes over time with detailed history charts. Know if you\'re getting a real deal.' },
  { icon: '📋', title: 'Track Multiple Items', desc: 'Keep all your watched products in one place. Add a product link once and we handle the rest.' },
  { icon: '🔗', title: 'Easy Item Tracking', desc: 'Paste a product URL from your browser or clipboard. Confirm details, set your desired price, and you\'re done.' },
  { icon: '🔒', title: 'Privacy-First', desc: 'We store only the minimal info needed to track items and deliver notifications. We do not sell your data.' },
];

const screenshots = [
  {
    src: '/images/price-tracker/price-tracker-screenshot-track-item.png',
    alt: 'Price Tracker – track item screen',
  },
  {
    src: '/images/price-tracker/price-tracker-screenshot-price-history.png',
    alt: 'Price Tracker – price history chart screen',
  },
  {
    src: '/images/price-tracker/price-tracker-screenshot-deal-alerts.png',
    alt: 'Price Tracker – deal alerts notification screen',
  },
  {
    src: '/images/price-tracker/price-tracker-screenshot-target-price.png',
    alt: 'Price Tracker – set target price screen',
  },
];

const PriceTrackerDealAlerts = () => (
  <div className="product-page">
    <SEO
      title="Price Tracker: Deal Alerts — Never Overpay Again | Appshow"
      description="Track prices and get deal alerts when they drop. Add a product link, set a target price, and shop at the right time with price history insights. Free for iOS and Android."
      keywords="price tracker, deal alerts, price drop notification, price history, shopping tracker, price comparison"
      url="/price-tracker-deal-alerts"
    />

    <section className="pp-hero">
      <div className="container pp-hero-inner">
        <div className="pp-hero-content">
        <div className="pp-hero-meta">
          <span className="badge-live">Live</span>
          <span className="pp-category">Shopping</span>
        </div>
        <h1 className="pp-title">Price Tracker: Deal Alerts</h1>
        <p className="pp-subtitle">Price Drop Alerts &amp; History</p>
        <p className="pp-description">
          Track prices and get deal alerts when they drop. Add a product once, choose your target price,
          and get timely alerts when the price drops — so you never miss a great deal across your
          favorite online stores.
        </p>
        <div className="pp-store-buttons">
          <a href="https://play.google.com/store/apps/details?id=com.appshow.pricedrop" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AndroidIcon />
            <span><span className="btn-store-sub">Get it on</span><span className="btn-store-main">Google Play</span></span>
          </a>
          <a href="https://apps.apple.com/us/app/price-tracker-deal-alerts/id6758753391" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AppleIcon />
            <span><span className="btn-store-sub">Download on the</span><span className="btn-store-main">App Store</span></span>
          </a>
        </div>
        <p className="pp-note">Free · iOS 15.1+ · Android · Shopping · Privacy-first</p>
        </div>
        <div className="pp-hero-preview">
          <img
            src="/images/price-tracker/price-tracker-deal-alerts-app-preview.png"
            alt="Price Tracker Deal Alerts app preview – track prices and get deal notifications"
            className="pp-preview-img"
          />
        </div>
      </div>
    </section>

    <section className="pp-screenshots">
      <div className="container">
        <h2 className="pp-section-title">Screenshots</h2>
        <div className="screenshots-row">
          {screenshots.map((s, i) => (
            <div className="phone-frame" key={i}>
              <img src={s.src} alt={s.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="pp-features">
      <div className="container">
        <h2 className="pp-section-title">Key Features</h2>
        <div className="features-grid">
          {features.map((f, i) => (
            <div className="feature-card" key={i}>
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="pp-cta">
      <div className="container">
        <h2>Never miss a deal again</h2>
        <p>Free to download. Add a product URL and start tracking in seconds.</p>
        <div className="pp-store-buttons">
          <a href="https://play.google.com/store/apps/details?id=com.appshow.pricedrop" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AndroidIcon /><span><span className="btn-store-sub">Get it on</span><span className="btn-store-main">Google Play</span></span>
          </a>
          <a href="https://apps.apple.com/us/app/price-tracker-deal-alerts/id6758753391" target="_blank" rel="noopener noreferrer" className="btn-store-large">
            <AppleIcon /><span><span className="btn-store-sub">Download on the</span><span className="btn-store-main">App Store</span></span>
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default PriceTrackerDealAlerts;
